import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  Star,
  Quote,
  Sparkles,
  ArrowRight,
  X,
  ChevronLeft,
  ChevronRight,
  Instagram,
  Heart,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import {
  getPublishedTestimonials,
  getPublishedStats,
  type PublicTestimonial,
  type PublicStat,
} from "@/lib/social-proof.functions";
import {
  TRUSTPILOT_PROFILE_URL,
  VERIFIED_TRUSTPILOT_REVIEWS,
} from "@/lib/trustpilot";
import reviewAvatarIan from "@/assets/review-avatar-ian.jpg";
import reviewAvatarSandy from "@/assets/review-avatar-sandy.jpg";

/** Illustrated cartoon avatars for the verified Trustpilot reviewers. */
const REVIEW_AVATARS: Record<string, string> = {
  Ian: reviewAvatarIan,
  SandyCroft: reviewAvatarSandy,
};

/** Comments shared by the community on Instagram. */
const COMMUNITY_VOICES: { username: string; comment: string }[] = [
  {
    username: "@heilsamaufbluehen",
    comment:
      "I wish people would understand that survival modes like fight, flight, freeze are real and really exhausting to deal with on a daily basis and have nothing to do with being lazy, or not positive enough.",
  },
  {
    username: "@lisamatt75",
    comment:
      "When people think you're grumpy but you're not — you just have a lot to deal with in your head.",
  },
  {
    username: "@commonhealthcg",
    comment:
      "Healing from trauma isn't always visible, and it definitely isn't a straight line. Some days can feel like progress, while other days may bring old emotions back — and that doesn't mean someone has failed. A little patience, understanding and compassion can make a bigger difference than we realize.",
  },
  {
    username: "@maysgrace2024",
    comment:
      "There are NO guidelines to healing! You heal in your own way at your own pace. Having a bad/off day is a setback, NOT a failure. Every single person's healing journey is different. Don't compare yours.",
  },
  {
    username: "@insidethearmor",
    comment:
      "The most powerful thing I learned from therapy was: what happened and what people did to me does not define me. You have a voice, you have a choice — this is just your story.",
  },
];

function instagramUrl(username: string) {
  return `https://www.instagram.com/${username.replace(/^@/, "")}`;
}


/** Truncated quote with an inline Read More / Show less toggle. */
function ExpandableQuote({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 110;

  return (
    <>
      <blockquote
        className={`font-body text-[13px] leading-relaxed text-foreground/85 ${
          !expanded ? "line-clamp-3" : ""
        }`}
      >
        “{text}”
      </blockquote>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-1.5 inline-flex w-fit items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] hover:underline underline-offset-4"
        >
          {expanded ? "Show less" : "Read More"}
<ArrowRight
            className={`h-3.5 w-3.5 transition-transform ${expanded ? "-rotate-90" : ""}`}
            aria-hidden
          />
        </button>
      )}
    </>
  );
}

const SLIDE_CLASS = "basis-[80%] pl-3 sm:basis-1/2 lg:basis-1/3";
const CARD_CLASS =
  "group flex h-full min-h-[170px] flex-col rounded-2xl border border-gold/30 bg-gradient-to-br from-card via-gold-light/20 to-card p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[var(--shadow-gold)] sm:p-3.5 lg:min-h-[230px] lg:p-6";
const CARD_CLASS_COMPACT =
  "group flex h-full min-h-[130px] flex-col rounded-2xl border border-gold/30 bg-gradient-to-br from-card via-gold-light/20 to-card p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[var(--shadow-gold)] lg:min-h-[160px]";


function initials(name: string) {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function StarRow({ rating, size = "sm" }: { rating: number; size?: "sm" | "lg" }) {
  const cls = size === "lg" ? "h-5 w-5" : "h-3.5 w-3.5";
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`${cls} ${
            i < rating ? "fill-[var(--gold-deep)] text-[var(--gold-deep)]" : "text-foreground/20"
          }`}
        />
      ))}
    </div>
  );
}

function AuthorChip({ t }: { t: PublicTestimonial }) {
  return (
    <div className="flex items-center gap-3">
      {t.avatar_url ? (
        <img loading="lazy" decoding="async"
          src={t.avatar_url}
          alt=""
          className="h-8 w-8 rounded-full border-2 border-gold/40 object-cover"
        />
      ) : (
        <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-gold/40 bg-[var(--gradient-gold)] font-display text-sm text-foreground shadow-[var(--shadow-gold)]">
          {initials(t.author_name) || "💛"}
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate font-display text-[13px] text-foreground">— {t.author_name}</div>
        {t.author_role && (
          <div className="truncate text-[11px] uppercase tracking-wider text-foreground/60">
            {t.author_role}
          </div>
        )}
      </div>
      {t.source && t.source !== "Community" && (
        <span className="ms-auto rounded-full bg-foreground/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-foreground/60">
          {t.source}
        </span>
      )}
    </div>
  );
}

function TestimonialCard({ t, onOpen }: { t: PublicTestimonial; onOpen: () => void }) {
  const isLong = t.quote.length > 140;

  return (
    <div className="group flex flex-col rounded-2xl border border-gold/30 bg-gradient-to-br from-card via-gold-light/20 to-card p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[var(--shadow-gold)] sm:p-3.5">
      <Quote className="h-4 w-4 text-[var(--gold-deep)]/70" aria-hidden />
      <StarRow rating={t.rating} />

      <div className="relative mt-3">
        <p className="font-body text-[13px] leading-relaxed text-foreground/90 line-clamp-3 sm:text-sm">
          "{t.quote}"
        </p>
        {isLong && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-card via-card/70 to-transparent"
          />
        )}
      </div>

      {isLong && (
        <button
          type="button"
          onClick={onOpen}
          className="mt-2 inline-flex w-fit items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] hover:underline underline-offset-4"
        >
          Read More <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}

<div className="mt-3">
        <AuthorChip t={t} />
      </div>
    </div>
  );
}

function TestimonialModal({
  testimonials,
  index,
  onIndexChange,
  onClose,
}: {
  testimonials: PublicTestimonial[];
  index: number | null;
  onIndexChange: (i: number) => void;
  onClose: () => void;
}) {
  const open = index !== null;
  const t = open ? testimonials[index!] : null;
  const canBrowse = testimonials.length > 1;

  const next = useCallback(() => {
    if (index === null) return;
    onIndexChange((index + 1) % testimonials.length);
  }, [index, onIndexChange, testimonials.length]);

  const prev = useCallback(() => {
    if (index === null) return;
    onIndexChange((index - 1 + testimonials.length) % testimonials.length);
  }, [index, onIndexChange, testimonials.length]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="w-[90vw] max-w-[640px] gap-0 overflow-hidden rounded-3xl border-2 border-gold/40 bg-gradient-to-br from-[oklch(0.98_0.015_85)] via-card to-[oklch(0.96_0.03_82)] p-0 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)] [&>button]:hidden"
      >
        {t && (
          <>
            <DialogHeader>
              <DialogTitle className="sr-only">Testimonial from {t.author_name}</DialogTitle>
              <DialogDescription className="sr-only">
                Full testimonial from {t.author_name}
              </DialogDescription>
            </DialogHeader>

            {/* Close (gold circular, top-right) */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close testimonial"
              className="absolute end-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)] ring-2 ring-gold/40 transition hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-gold/50"
            >
              <X className="h-5 w-5" strokeWidth={2.5} />
            </button>

            <div className="max-h-[85vh] overflow-y-auto px-6 py-10 sm:px-10 sm:py-12">
              <div className="flex flex-col items-center text-center">
                <Quote className="h-9 w-9 text-[var(--gold-deep)]/70" aria-hidden />
                <div className="mt-3">
                  <StarRow rating={t.rating} size="lg" />
                </div>
              </div>

              <blockquote className="mx-auto mt-6 max-w-prose whitespace-pre-line font-body text-[15px] leading-[1.8] text-foreground/90 sm:text-base sm:leading-[1.85]">
                "{t.quote}"
              </blockquote>

              <div className="mx-auto mt-8 h-px w-16 bg-gold/40" />

              <div className="mt-6 text-center">
                <div className="font-display text-base text-foreground">— {t.author_name}</div>
                {t.author_role && (
                  <div className="mt-0.5 text-xs text-foreground/65">{t.author_role}</div>
                )}
                {t.source && (
                  <div className="mt-2 text-[11px] uppercase tracking-[0.15em] text-foreground/55">
                    {t.source}
                  </div>
                )}
              </div>
            </div>

            {canBrowse && (
              <div className="flex items-center justify-between gap-3 border-t border-gold/20 bg-cream/40 px-4 py-3 sm:px-6">
                <button
                  type="button"
                  onClick={prev}
                  className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-card/80 px-3 py-1.5 text-xs font-medium text-foreground/80 transition hover:border-gold hover:text-foreground"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                <span className="text-[11px] uppercase tracking-wider text-foreground/55">
                  {index! + 1} / {testimonials.length}
                </span>
                <button
                  type="button"
                  onClick={next}
                  className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-card/80 px-3 py-1.5 text-xs font-medium text-foreground/80 transition hover:border-gold hover:text-foreground"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


export function TestimonialsSection({ compact = false }: { compact?: boolean }) {
  const fetchTestimonials = useServerFn(getPublishedTestimonials);
  const fetchStats = useServerFn(getPublishedStats);
  const [testimonials, setTestimonials] = useState<PublicTestimonial[]>([]);
  const [stats, setStats] = useState<PublicStat[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [slideCount, setSlideCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setSlideCount(api.scrollSnapList().length);
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  useEffect(() => {
    let cancel = false;
    Promise.all([fetchTestimonials().catch(() => []), fetchStats().catch(() => [])]).then(
      ([t, s]) => {
        if (cancel) return;
        setTestimonials(t);
        setStats(s);
        setLoaded(true);
      },
    );
    return () => {
      cancel = true;
    };
  }, [fetchTestimonials, fetchStats]);

  // Hide entirely until we know what we have.
  if (!loaded) return null;

  return (
    <section className={compact ? "mt-4 min-w-0 w-full" : "mt-6 min-w-0 w-full lg:mt-10"}>
      <div className="mx-auto w-full min-w-0 max-w-5xl lg:max-w-[1180px]">
        <div className="text-center">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full bg-foreground font-semibold uppercase tracking-[0.14em] text-background ${
              compact ? "px-2.5 py-0.5 text-[10px]" : "px-3 py-1 text-[11px]"
            }`}
          >
            <Sparkles className="h-3 w-3" /> Community voices
          </span>
          <h2
            className={
              compact
                ? "mt-1.5 font-display text-base leading-tight tracking-tight text-foreground sm:text-lg"
                : "mt-2 font-display text-xl leading-tight tracking-tight text-foreground sm:text-2xl lg:font-['Outfit'] lg:text-4xl lg:font-semibold"
            }
          >
            What Our Community Is Saying
          </h2>

          <p
            className={
              compact
                ? "mx-auto mt-1 max-w-lg text-[10px] leading-relaxed text-foreground/70 sm:text-[11px]"
                : "mx-auto mt-1.5 max-w-xl text-xs text-foreground/70 sm:text-sm lg:mt-3 lg:max-w-2xl lg:font-['Figtree'] lg:text-base lg:leading-relaxed"
            }
          >
            Real voices from the Recovery Trauma™ community. We asked trauma
            survivors what they wish more people understood about trauma and
            healing.
          </p>
        </div>

        {(
          <div className={compact ? "mt-3" : "mt-4"}>
            <Carousel
              setApi={setApi}
              opts={{
                align: "start",
                loop: true,
                dragFree: false,
              }}
              className="w-full"
              aria-label="Community testimonials"
            >
              <CarouselContent className="-ms-3">
                {/* Verified Trustpilot reviews — real, unedited */}
                {[...VERIFIED_TRUSTPILOT_REVIEWS]
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map((review) => (
                    <CarouselItem
                      key={`${review.author}-${review.date}`}
                      className={SLIDE_CLASS}
                    >
                      <article className={compact ? CARD_CLASS_COMPACT : CARD_CLASS}>
                        <div className="flex items-center gap-2">
                          {REVIEW_AVATARS[review.author] && (
                            <img
                              src={REVIEW_AVATARS[review.author]}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              width={512}
                              height={512}
                              className="h-8 w-8 shrink-0 rounded-full border-2 border-gold/40 object-cover"
                            />
                          )}
                          <span className="flex gap-0.5" aria-label={`${review.rating} out of 5 stars`}>
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <Star
                                key={i}
                                className="h-3.5 w-3.5 fill-[var(--gold-deep)] text-[var(--gold-deep)]"
                                aria-hidden
                              />
                            ))}
                          </span>
                          <span className="ms-auto rounded-full bg-foreground/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-foreground/60">
                            Trustpilot
                          </span>
                        </div>
                        <h3 className="mt-2 text-[13px] font-semibold text-foreground">
                          {review.title}
                        </h3>
                        <ExpandableQuote text={review.body} />
                        <p className="mt-2 text-[11px] text-foreground/60">
                          {review.author} · {review.location} ·{" "}
                          {new Date(review.date).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                      </article>
                    </CarouselItem>
                  ))}

                {testimonials.map((t, i) => (
                  <CarouselItem
                    key={t.id}
                    className="basis-[80%] ps-3 sm:basis-1/2 lg:basis-1/3"
                  >
                    <TestimonialCard t={t} onOpen={() => setOpenIndex(i)} />
                  </CarouselItem>
                ))}

                {/* Community voices shared on Instagram */}
                {COMMUNITY_VOICES.map((voice) => (
                  <CarouselItem key={voice.username} className={SLIDE_CLASS}>
                    <article className={CARD_CLASS}>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]">
                          <Instagram className="h-4 w-4" strokeWidth={1.9} />
                        </span>
                        <a
                          href={instagramUrl(voice.username)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`View ${voice.username} on Instagram (opens in a new tab)`}
                          className="truncate text-[13px] font-semibold text-foreground underline-offset-4 transition-colors hover:text-[var(--gold-deep)] hover:underline"
                        >
                          {voice.username}
                        </a>
                      </div>
<ExpandableQuote text={voice.comment} />
                    </article>
                  </CarouselItem>
                ))}

                {/* Closing card — the reader's own voice */}
                <CarouselItem className={SLIDE_CLASS}>
                  <article
                    className={`${compact ? CARD_CLASS_COMPACT : CARD_CLASS} items-center justify-center text-center`}
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]">
                      <Heart className="h-5 w-5" strokeWidth={1.9} />
                    </span>
                    <h3 className="mt-2.5 font-display text-base leading-snug tracking-wide text-foreground">
                      Your Story Matters 💜
                    </h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-foreground/80">
                      Your voice might help someone else feel seen, understood
                      and less alone.
                    </p>
                  </article>
                </CarouselItem>
              </CarouselContent>
              {(
                <>
                  <CarouselPrevious
                    aria-label="Previous testimonial"
                    className="flex -start-3 h-[44px] w-[44px] border-gold/40 bg-card/90 text-[var(--gold-deep)] shadow-[var(--shadow-gold)] hover:bg-cream"
                  />
                  <CarouselNext
                    aria-label="Next testimonial"
                    className="flex -end-3 h-[44px] w-[44px] border-gold/40 bg-card/90 text-[var(--gold-deep)] shadow-[var(--shadow-gold)] hover:bg-cream"
                  />
                </>
              )}
            </Carousel>

            {/* Mobile-only swipe hint */}
            {slideCount > 1 && (
              <p className="mt-3 text-center text-[11px] uppercase tracking-wider text-foreground/50 sm:hidden">
                ← Swipe to see more →
              </p>
            )}
          </div>
        )}

        <p className="mx-auto mt-3 max-w-xl text-center text-xs leading-relaxed text-foreground/70">
          Thank you to everyone who shared their experiences. Your words may
          help another person feel seen, understood and less alone.
        </p>

        {VERIFIED_TRUSTPILOT_REVIEWS.length > 0 && (
          <a
            href={TRUSTPILOT_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mx-auto mt-2 flex w-fit items-center justify-center gap-1.5 text-xs font-medium text-foreground/70 transition-colors hover:text-foreground"
          >
            Read all reviews on Trustpilot
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        )}
      </div>


      <TestimonialModal
        testimonials={testimonials}
        index={openIndex}
        onIndexChange={setOpenIndex}
        onClose={() => setOpenIndex(null)}
      />
    </section>
  );
}
